'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useAuth } from '../contexts/AuthContext'
import { authenticatedFetch } from '@/lib/client-fetch'

export default function ReportProblemPage() {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    description: '',
    userEmail: user?.email || '',
    userName: user?.name || ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (!formData.category || !formData.subject || !formData.description) {
        setError('Please fill in all required fields')
        setLoading(false)
        return
      }

      const response = await authenticatedFetch('/api/reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user?.id || null,
          userEmail: formData.userEmail || null,
          userName: formData.userName || null,
          category: formData.category,
          subject: formData.subject,
          description: formData.description
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit report')
      }

      setSuccess(true)
      setFormData({
        category: '',
        subject: '',
        description: '',
        userEmail: user?.email || '',
        userName: user?.name || ''
      })

      setTimeout(() => {
        setSuccess(false)
      }, 5000)
    } catch (err: any) {
      setError(err.message || 'Failed to submit report. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cream text-ink">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-ink/[0.06] bg-cream/85 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="font-serif italic font-semibold text-2xl text-ink tracking-tight">
            Deeliver
          </Link>
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link href="/about" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              About
            </Link>
            <Link href="/how-it-works" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              How it works
            </Link>
            <Link href="/faq" className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]">
              FAQ
            </Link>
            {user ? (
              <Link 
                href={
                  user.type === 'student' ? '/student/orders' :
                  user.type === 'parent' ? '/rider/new-order' :
                  user.type === 'driver' ? '/driver/schedule' :
                  user.type === 'admin' ? '/admin' : '/login'
                }
                className="text-ink-soft hover:text-ink transition text-sm font-medium flex items-center min-h-[44px]"
              >
                Dashboard
              </Link>
            ) : null}
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-ember focus:ring-offset-2 focus:ring-offset-cream min-h-[44px] flex items-center justify-center"
            >
              Start an order
            </Link>
          </div>
          <div className="md:hidden">
            <Link 
              href="/rider/new-order" 
              className="bg-ember text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-ember-hover transition-colors"
            >
              Order
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink mb-3">Report a Problem</h1>
          <p className="text-ink-soft">Help us improve by reporting issues or sharing feedback</p>
          <p className="mt-3 text-sm text-gray-700">
            Contact{' '}
            <a
              href="mailto:deeliveradmin@gmail.com"
              className="text-ember underline hover:text-ember-hover"
            >
              deeliveradmin@gmail.com
            </a>{' '}
            or report the problem here.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-cream-deep shadow-sm p-8">
          {success && (
            <div className="mb-6 bg-green-50 border border-green-300 text-green-700 px-4 py-3 rounded-lg text-sm">
              Thank you for your report! We've received it and will review it shortly.
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Information (if not logged in) */}
            {!user && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm mb-2 font-medium">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm mb-2 font-medium">
                    Your Email
                  </label>
                  <input
                    type="email"
                    value={formData.userEmail}
                    onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                    className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
                    placeholder="Optional"
                  />
                </div>
              </div>
            )}

            {/* Category */}
            <div>
              <label className="block text-gray-700 text-sm mb-2 font-medium">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
                required
              >
                <option value="">Select a category</option>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="payment">Payment Issue</option>
                <option value="delivery">Delivery Issue</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-gray-700 text-sm mb-2 font-medium">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember"
                placeholder="Brief summary of the issue"
                required
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-gray-700 text-sm mb-2 font-medium">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-white text-gray-900 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-ember focus:border-ember min-h-[150px]"
                placeholder="Please provide as much detail as possible..."
                required
                maxLength={2000}
              />
              <p className="text-gray-500 text-xs mt-1">
                {formData.description.length}/2000 characters
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-ember text-white px-6 py-3 rounded-full font-semibold hover:bg-ember-hover transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-ember focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {loading ? 'Submitting...' : 'Submit Report'}
            </button>
          </form>
        </div>

        <div className="mt-8 text-center">
          <Link href="/" className="text-ember hover:text-ember-hover text-sm font-medium">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  )
}

